export type ContentType =
  | 'home'
  | 'search'
  | 'feature'
  | 'informational'
  | 'contact'
  | 'compare'
  | 'vehicle'
  | 'ratings';

export type ContentTrackingMeta = {
  content_group: string;
  content_group2?: string;
  content_group3?: string;
  content_group4?: string;
  content_type: ContentType;
};

type ContentEvent = ContentTrackingMeta & {
  event: 'content';
  incremental_page_view: number;
};

export const SESSION_KEY = 'incremental_page_view';

/**
 * logTrackingEvent - Log custom tracking event
 *
 * Initialises `window.dataLayer` if it doesn't yet exist, then pushes.
 * This matches gtag.js's documented queue-and-replay contract: events
 * pushed before `gtag/js` loads sit in the array, and gtag.js processes
 * the queue when it loads.
 *
 * The seed is required because HSIYC fires page-view events from React
 * mount lifecycles (V1 `componentDidMount`, V2 `useEffect`), both of
 * which run before `<Script strategy="afterInteractive">` executes its
 * gtag-init seed. A guard like `Array.isArray(window.dataLayer)` would
 * silently drop these mount-fired events on hard refresh.
 *
 * @param {unknown} eventObject
 */
const logTrackingEvent = (eventObject: unknown) => {
  if (typeof window === 'undefined') return;
  if (!window.dataLayer) {
    window.dataLayer = [];
  }
  window.dataLayer.push(eventObject);
};

const getNextIncrementalPageView = (): number => {
  if (typeof window === 'undefined') return 1;

  const raw = window.sessionStorage.getItem(SESSION_KEY);
  const current = raw ? Number(raw) : 0;
  const next = Number.isFinite(current) ? current + 1 : 1;

  window.sessionStorage.setItem(SESSION_KEY, String(next));

  return next;
};

const logPageViewEvent = (meta: ContentTrackingMeta) => {
  const eventObject: ContentEvent = {
    event: 'content',
    content_group: meta.content_group,
    content_group2: meta.content_group2,
    content_group3: meta.content_group3,
    content_group4: meta.content_group4,
    content_type: meta.content_type,
    incremental_page_view: getNextIncrementalPageView(),
  };

  logTrackingEvent(eventObject);
};

type ClickEventProps = {
  eventText: string;
  eventUrl?: string;
};
export const logButtonClickEvent = ({ eventText, eventUrl }: ClickEventProps) => {
  logTrackingEvent({
    event: 'interaction_click',
    component_name: 'button',
    click_text: eventText,
    click_url: eventUrl,
  });
};

export { logTrackingEvent, logPageViewEvent, getNextIncrementalPageView };
